import React, { useState, useEffect } from 'react';
import Router from 'next/router';
import Head from 'next/head';
import { Provider } from 'mobx-react';
import { ApolloProvider } from 'react-apollo';
import NProgress from 'nprogress';
import gql from 'graphql-tag';
import 'moment/locale/pt-br';

/* Internal */
import Loading from '../components/Loading';
import AuthProvider from '../contexts/AuthProvider';
import { useStore, initializeRootStore } from '../lib/rootStore';
import { zendesk } from '../lib/zendesk';
import { getApolloClient, withApollo } from '../lib/apollo';
import { destroyCookie, getCookie, setCookie, getUserData } from '../lib/Util';
import '../styles/EmptyLayout.scss';
import '../styles/Invoice.scss';
import '../styles/MainLayout.scss';
import '../styles/Plan.scss';
import '../styles/ServiceContract.scss';
import '../styles/Settings.scss';
import '../styles/index.scss';
import '../styles/nprogress.css';

// Time in seconds
const getAccessTokenLifetime = () => {
  if (process.env.NODE_ENV === 'development') return 60 * 60 * 10; // 10 hours
  return 60 * 30; // 30 minutes
};

NProgress.configure({ showSpinner: false });

Router.onRouteChangeStart = (url) => {
  console.info(`Loading: ${url}`);
  NProgress.start();
};
Router.onRouteChangeComplete = () => NProgress.done();
Router.onRouteChangeError = () => NProgress.done();

const logout = (context) => {
  destroyCookie(context, 'selectedOrganizationId', { path: '/' });
  destroyCookie(context, 'accessToken');

  // eslint-disable-next-line no-restricted-globals
  if (typeof location !== 'undefined') {
    // eslint-disable-next-line no-restricted-globals
    const { hostname } = location;

    destroyCookie(context, 'refresh_token');
    destroyCookie(context, 'selectedOrganizationId', { path: '/' });

    const domain = `.${hostname.split('.').slice(1).join('.')}`;
    destroyCookie(context, 'refresh_token', { path: '/', domain });
    destroyCookie(context, 'selectedOrganizationId', { path: '/' });
  } else if (process.env.NODE_ENV === 'development') {
    destroyCookie(context, 'refresh_token');
    destroyCookie(context, 'selectedOrganizationId', { path: '/' });
  } else if (process.env.NODE_ENV === 'stage') {
    destroyCookie(context, 'refresh_token', { path: '/', domain: '.classapp.ninja' });
    destroyCookie(context, 'selectedOrganizationId', { path: '/', domain: 'krabs.classapp.ninja' });
  } else if (process.env.NODE_ENV === 'internal') {
    destroyCookie(context, 'refresh_token', { path: '/', domain: '.classapp.ninja' });
    destroyCookie(context, 'selectedOrganizationId', { path: '/', domain: 'krabs-internal.classapp.ninja' });
  } else {
    destroyCookie(context, 'refresh_token', { path: '/', domain: '.classapp.com.br' });
    destroyCookie(context, 'refresh_token', { path: '/', domain: '.classapp.co' });
    destroyCookie(context, 'selectedOrganizationId', { path: '/', domain: 'financeiro.classapp.com.br' });
  }
};

const createBotEntity = async ({ accessToken, organizationId }) => {
  await getApolloClient(null, {
    Authorization: `Bearer ${accessToken}`
  }).mutate({
    mutation: gql`mutation KrabsCreateBot {
      createEntityTemplate(input: {
        type: RECURRENCE_BOT, organizationId: ${organizationId}
      }) {
        clientMutationId
      }
    }`
  }).catch(e => console.error(e));
};

const App = (props) => {
  const {
    Component, pageProps, user, router, apollo
  } = props;
  const [store, setStore] = useState(null);
  const initialStore = useStore((pageProps || {}).initialState);
  const errorMessage = user?.notAuthorized ? 'NO_ENTITY' : null;

  if (store) {
    store.mainStore.logout = async (context) => {
      await router.push('/');
      logout(context);
      store.mainStore.reset();
      setStore(initializeRootStore((pageProps || {}).initialState));
    };

    store.mainStore.changeSelectedOrganization = (newOrganization, skipRedirect = false) => {
      store.mainStore.setUser({
        ...store.mainStore.user,
        prevSelectedOrganizationId: store.mainStore.user.selectedOrganizationId,
        selectedOrganizationId: newOrganization.id
      });

      setCookie(null, 'selectedOrganizationId', newOrganization.id);

      if (!skipRedirect) {
        if (router.route.match(/^\/(entity|product|plan|settings)(\/|$)/)) {
          const target = router.route.split('/').slice(0, 2).join('/');
          if (target.replace(/\//g, '') !== router.route.replace(/\//g, '')) {
            router.push(target);
          }
        } else {
          router.push('/');
        }
      }

      createBotEntity({
        accessToken: getCookie(null, 'accessToken'),
        organizationId: newOrganization.id
      });
    };
  }

  useEffect(() => {
    initialStore.mainStore.setUser(user);
    initialStore.mainStore.setErrorMessage(errorMessage);
    initialStore.mainStore.setIsMenuExpanded(getCookie(null, 'isMenuExpanded') === 'true');

    setStore(initialStore);

    if (errorMessage) router.push('/');

    // Automatic logoff
    setInterval(() => {
      const accessToken = getCookie(null, 'accessToken');
      if (!accessToken && user && (user.organizations.length + user.organizationsToContract.length)
        && !['', 'serviceContract', 'login'].includes(router.route.split('/')[0])) {
        initialStore.mainStore.logout(null);
      }
    }, 10000);
  }, []);

  if (!store) return <Loading height={80} width={80} />;

  // Renew cookie
  if (store.mainStore.user && Object.keys(store.mainStore.user).length > 0) {
    const accessToken = getCookie(null, 'accessToken');
    if (accessToken) {
      setCookie(null, 'accessToken', accessToken, getAccessTokenLifetime());
    } else {
      store.mainStore.logout(null);
    }
  }

  return (
    <div>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <Provider store={initialStore.mainStore} filterStore={initialStore.filterStore} client={apollo}>
        <AuthProvider>
          <ApolloProvider client={apollo}>
            <Component {...pageProps} router={router} />
          </ApolloProvider>
        </AuthProvider>
      </Provider>
    </div>
  );
};

const getAccessTokenFromCode = async (context, oauthCode) => {
  let accessToken = getCookie(context, 'accessToken');
  if (oauthCode && !accessToken) {
    const user = await getApolloClient(null, null).mutate({
      mutation: gql`mutation KrabsAuthenticate {
        codeAuthenticate(input: {code: "${oauthCode}"}) {
          user {
            dbId
            fullname
            oauthProvider { accessToken }
          }
        }
      }`
    }).then(response => response.data.codeAuthenticate.user);

    if (user?.oauthProvider) {
      // eslint-disable-next-line prefer-destructuring
      accessToken = user.oauthProvider.accessToken;
      setCookie(context, 'accessToken', accessToken, getAccessTokenLifetime());
    }
  }
  return accessToken;
};

App.getInitialProps = async ({ ctx, Component }) => {
  let { user } = initializeRootStore();
  let accessToken;

  try {
    accessToken = await getAccessTokenFromCode(ctx, ctx.query?.code);
  } catch (e) {
    console.error(e);
  }

  if (accessToken) {
    user = await getUserData(accessToken, ctx) || user;

    if (user.selectedOrganizationId) {
      createBotEntity({ accessToken, organizationId: user.selectedOrganizationId });
    }

    if (!user || !user.organizations) {
      logout(ctx);
    }
  }

  if (user) {
    zendesk.open(user);
  } else {
    zendesk.close();
  }
  let initialProps = {};
  if (Component.getInitialProps) {
    initialProps = await Component.getInitialProps(ctx);
  }

  return {
    user,
    ...initialProps
  };
};

export default withApollo()(App);
